import { addDays } from '../dates.js';
import { NUMBER_WORDS } from './number-words.js';

const WEEKDAYS: Record<string, number> = {
  sun: 0, sunday: 0, dim: 0, dimanche: 0,
  mon: 1, monday: 1, lun: 1, lundi: 1,
  tue: 2, tues: 2, tuesday: 2, mar: 2, mardi: 2,
  wed: 3, weds: 3, wednesday: 3, mer: 3, merc: 3, mercredi: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4, jeu: 4, jeud: 4, jeudi: 4,
  fri: 5, friday: 5, ven: 5, vend: 5, vendredi: 5,
  sat: 6, saturday: 6, sam: 6, samedi: 6,
};

function dayOfWeek(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export interface DateMatch {
  iso: string;
  raw: string;       // texte matché à retirer du titre
  start: number;
  end: number;
}

const PATTERNS: Array<(text: string, today: string) => DateMatch | null> = [
  // @2026-04-25 or 2026-04-25
  (text) => {
    const m = text.match(/@?(\d{4}-\d{2}-\d{2})\b/);
    if (!m) return null;
    return { iso: m[1]!, raw: m[0], start: m.index!, end: m.index! + m[0].length };
  },
  // DD/MM (next occurrence) or DD/MM/YY or DD/MM/YYYY
  // Optionally consumes a leading filler word ("starting", "from",
  // "dès le", "à partir du", "le") so the title stays clean.
  (text, today) => {
    const m = text.match(
      /\b(?:(?:starting|from|dès\s+le|d[eè]s|à\s+partir\s+du|a\s+partir\s+du|le)\s+)?(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?\b/i
    );
    if (!m) return null;
    const day = parseInt(m[1]!, 10);
    const month = parseInt(m[2]!, 10);
    if (day < 1 || day > 31 || month < 1 || month > 12) return null;
    const [ty, tm, td] = today.split('-').map(Number) as [number, number, number];
    let year: number;
    if (m[3]) {
      const raw = parseInt(m[3], 10);
      year = m[3].length === 2 ? 2000 + raw : raw;
    } else {
      year = ty;
      if (month < tm || (month === tm && day < td)) year += 1;
    }
    const dt = new Date(Date.UTC(year, month - 1, day));
    if (
      dt.getUTCFullYear() !== year ||
      dt.getUTCMonth() !== month - 1 ||
      dt.getUTCDate() !== day
    ) {
      return null;
    }
    const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return { iso, raw: m[0], start: m.index!, end: m.index! + m[0].length };
  },
  // @today, today, tom, Tom, tomorrow, aujourd'hui, demain
  (text, today) => {
    const m = text.match(
      /@?\b(today|aujourd['’]?hui|auj|tomorrow|tom|demain)\b/i
    );
    if (!m) return null;
    const word = m[1]!.toLowerCase();
    const offset = word.startsWith('tom') || word.startsWith('dem') ? 1 : 0;
    return {
      iso: addDays(today, offset),
      raw: m[0],
      start: m.index!,
      end: m.index! + m[0].length,
    };
  },
  // @monday, next monday, lundi, mardi prochain (includes prefixes like thu, thur, thurs)
  (text, today) => {
    const names = Object.keys(WEEKDAYS)
      // sort longer first so greedy match picks the longest form
      .sort((a, b) => b.length - a.length)
      .join('|');
    const re = new RegExp(`@?\\b(?:next\\s+|prochain\\s+)?(${names})(?:\\s+prochain)?\\b`, 'i');
    const m = text.match(re);
    if (!m) return null;
    const target = WEEKDAYS[m[1]!.toLowerCase()]!;
    const current = dayOfWeek(today);
    let delta = (target - current + 7) % 7;
    if (delta === 0) delta = 7; // "monday" from monday → next monday
    return {
      iso: addDays(today, delta),
      raw: m[0],
      start: m.index!,
      end: m.index! + m[0].length,
    };
  },
  // in N days/weeks/months (EN) — dans N jours/semaines/mois (FR)
  // N can be a digit ("2") or a word ("two", "deux"), incl. "a/an/une".
  // Months = same day-of-month N months later, clamped to last valid day.
  (text, today) => {
    const m = text.match(
      /\b(?:in|dans)\s+(\d+|an?|une?|one|two|three|four|five|six|seven|eight|nine|ten|deux|trois|quatre|cinq|sept|huit|neuf|dix)\s+(days?|jours?|weeks?|semaines?|months?|mois)\b/i
    );
    if (!m) return null;
    const word = m[1]!.toLowerCase();
    const n = /^\d+$/.test(word)
      ? parseInt(word, 10)
      : word === 'a' || word === 'an' || word === 'une' || word === 'un'
      ? 1
      : NUMBER_WORDS[word];
    if (!Number.isFinite(n)) return null;
    const unit = m[2]!.toLowerCase();
    let iso: string;
    if (unit.startsWith('month') || unit === 'mois') {
      const [y, mo, d] = today.split('-').map(Number) as [number, number, number];
      const targetMonthIdx = mo - 1 + (n as number);
      const targetY = y + Math.floor(targetMonthIdx / 12);
      const targetM = ((targetMonthIdx % 12) + 12) % 12;
      const lastDay = new Date(Date.UTC(targetY, targetM + 1, 0)).getUTCDate();
      const day = Math.min(d, lastDay);
      iso = `${targetY}-${String(targetM + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    } else {
      const mult = unit.startsWith('week') || unit.startsWith('semaine') ? 7 : 1;
      iso = addDays(today, (n as number) * mult);
    }
    return {
      iso,
      raw: m[0],
      start: m.index!,
      end: m.index! + m[0].length,
    };
  },
];

export function findDate(text: string, today: string): DateMatch | null {
  for (const p of PATTERNS) {
    const m = p(text, today);
    if (m) return m;
  }
  return null;
}
